import React, { useEffect, useState, useRef } from 'react';
import { Search, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const diputadoNames = ['Camila Rojas', 'Eric Aedo', 'Joaquin Lavin', 'Maria Calendaria', 'Alessandri', 'Chiara Barchiesi', 'Johannes Kaiser', 'Jose Carlos Meza', 'Diego Ibañez', 'Maite Orsini'];
interface SearchResult {
  id: string;
  nombre: string;
  partido: string;
  bancada: string[];
  image_path: string | null;
}
interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  results?: SearchResult[];
  isSearching?: boolean;
}
export function SearchInput({
  value,
  onChange,
  results = [],
  isSearching = false
}: SearchInputProps) {
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    const currentName = diputadoNames[placeholderIndex];
    const typingSpeed = isDeleting ? 50 : 100;
    const pauseTime = 2000;
    if (!isDeleting && charIndex === currentName.length) {
      const timeout = setTimeout(() => setIsDeleting(true), pauseTime);
      return () => clearTimeout(timeout);
    }
    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setPlaceholderIndex(prev => (prev + 1) % diputadoNames.length);
      return;
    }
    const timeout = setTimeout(() => {
      if (isDeleting) {
        setDisplayText(currentName.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
      } else {
        setDisplayText(currentName.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
      }
    }, typingSpeed);
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, placeholderIndex]);
  useEffect(() => {
    setShowDropdown(value.trim().length > 0 && results.length > 0);
    setSelectedIndex(-1);
    setImageErrors({});
  }, [value, results]);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSelectResult = (id: string) => {
    setShowDropdown(false);
    setSelectedIndex(-1);
    onChange('');
    navigate(`/diputado?id=${id}`);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || results.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => prev < results.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelectResult(results[selectedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };
  const handleImageError = (id: string) => {
    setImageErrors(prev => ({
      ...prev,
      [id]: true
    }));
  };
  return <div className="w-full" ref={dropdownRef} data-id="element-157">
      {/* Header Label */}
      <label className="block mb-2" data-id="element-158">
        <span className="font-display text-sm font-bold text-purple uppercase tracking-wide" data-id="element-159">
          Busca a tu diputado
        </span>
      </label>

      <div className="relative" data-id="element-160">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-161">
          <Search className="w-5 h-5 text-purple" data-id="element-162" />
        </div>
        <input ref={inputRef} type="text" value={value} onChange={e => onChange(e.target.value)} onKeyDown={handleKeyDown} onFocus={() => value.trim().length > 0 && results.length > 0 && setShowDropdown(true)} placeholder={`${displayText}${!isDeleting && charIndex === diputadoNames[placeholderIndex].length ? '' : '|'}`} className="w-full bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal pl-12 pr-4 py-4 text-base text-[#1A1A1A] placeholder:text-lavender focus:outline-none focus:border-purple focus:shadow-brutal-hover transition-all duration-100" data-id="element-163" />

        {/* Dropdown Menu */}
        {showDropdown && <div className="absolute top-full left-0 right-0 mt-2 bg-white border-brutal border-[#1A1A1A] rounded-none shadow-brutal max-h-96 overflow-y-auto z-50" data-id="element-164">
            {isSearching ? <div className="px-6 py-4 text-center" data-id="element-165">
                <span className="font-display text-sm text-purple uppercase tracking-wide animate-pulse" data-id="element-166">
                  Buscando...
                </span>
              </div> : <>
                {results.map((result, index) => <button key={result.id} onClick={() => handleSelectResult(result.id)} className={`w-full px-6 py-4 text-left border-b-2 border-[#1A1A1A] last:border-b-0 transition-colors duration-100 focus:outline-none ${index === selectedIndex ? 'bg-yellow' : 'hover:bg-yellow'}`} data-id="element-167">
                    <div className="flex items-center gap-4" data-id="element-168">
                      {/* Diputado Image */}
                      <div className="w-12 h-12 bg-lavender border-2 border-[#1A1A1A] rounded-none flex items-center justify-center flex-shrink-0 overflow-hidden" data-id="element-169">
                        {result.image_path && !imageErrors[result.id] ? <img src={result.image_path} alt={result.nombre} className="w-full h-full object-cover" onError={() => handleImageError(result.id)} data-id="element-170" /> : <User className="w-6 h-6 text-purple" data-id="element-171" />}
                      </div>

                      {/* Diputado Info */}
                      <div className="flex-1 min-w-0" data-id="element-172">
                        <div className="font-display font-bold text-purple text-base mb-1" data-id="element-173">
                          {result.nombre}
                        </div>
                        <div className="text-sm text-[#1A1A1A]" data-id="element-174">
                          <span className="font-display uppercase" data-id="element-175">
                            {result.partido}
                          </span>
                          {' • '}
                          <span data-id="element-176">{result.bancada.join(', ')}</span>
                        </div>
                      </div>
                    </div>
                  </button>)}
              </>}
          </div>}
      </div>

      {/* Fixed height container for status messages to prevent layout shift */}
      <div className="mt-2 h-5 flex items-center justify-center" data-id="element-177">
        {/* Searching message */}
        {value && !showDropdown && isSearching && <span className="font-display text-xs text-purple uppercase tracking-wide animate-pulse" data-id="element-178">
            Buscando...
          </span>}

        {/* No results message */}
        {value && !showDropdown && !isSearching && results.length === 0 && <span className="font-display text-xs text-[#1A1A1A] uppercase tracking-wide" data-id="element-179">
            No se encontraron resultados
          </span>}
      </div>
    </div>;
}